import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Cube, Sparkle, File, TreeStructure, PaintBrush, Database, ArrowRight, CircleNotch,
  Play, Code, ArrowSquareOut, DownloadSimple, DeviceMobile, Globe, Copy, Check, RocketLaunch, Terminal,
  UploadSimple, ShieldCheck, Bug, AppStoreLogo, GooglePlayLogo, X, Warning, FilmSlate, Lightbulb, VideoCamera, Stack, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MODES = [
  { id: "web", label: "Web App", icon: Globe, hint: "Full-stack React + FastAPI web app with live preview" },
  { id: "mobile", label: "Mobile App", icon: DeviceMobile, hint: "Expo / React Native app (iOS + Android) with phone-frame preview" },
  { id: "video", label: "Long AI Video", icon: VideoCamera, hint: "Multi-scene AI video from a prompt (MP4)" },
  { id: "cinema", label: "AI Cinema", icon: FilmSlate, hint: "Cinematic AI film with storyboard + narration" },
  { id: "brainstorm", label: "Brainstorm", icon: Lightbulb, hint: "Generate 5 product concepts — pick one to build" },
];

const SAMPLES = [
  "A habit tracker with streaks and daily reminders",
  "A kanban task board with drag columns",
  "An expense splitter for roommates with balances",
  "A pomodoro focus timer with session history",
];

const AGENTS = [
  { name: "Architect", icon: TreeStructure, color: "#F29A86" },
  { name: "Designer", icon: PaintBrush, color: "#A3C2A4" },
  { name: "Developer", icon: Cube, color: "#F4D399" },
  { name: "QA", icon: Sparkle, color: "#FFB8A5" },
];

const TABS = [
  { id: "preview", label: "Live Preview", icon: Play },
  { id: "code", label: "Code", icon: Code },
  { id: "blueprint", label: "Blueprint", icon: TreeStructure },
  { id: "review", label: "QA Review", icon: ShieldCheck },
  { id: "publish", label: "Publish", icon: RocketLaunch },
];

export default function AppBuilder() {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [mode, setMode] = useState("web");
  const [queue, setQueue] = useState([]);
  const [messages, setMessages] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [modifyTarget, setModifyTarget] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [build, setBuild] = useState(null);
  const [activeFile, setActiveFile] = useState(0);
  const [tab, setTab] = useState("preview");
  const [copied, setCopied] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(0);
  const attachRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const loadQueue = useCallback(async () => {
    try { const r = await api.get("/builds/queue"); setQueue(r.data.queue || []); } catch { /* ignore */ }
  }, []);
  const loadGallery = useCallback(async () => {
    try { const r = await api.get("/builds"); setGallery((r.data || []).filter((b) => b.spec && b.status === "done")); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
    loadQueue(); loadGallery();
    const iv = setInterval(loadQueue, 3000);
    return () => clearInterval(iv);
  }, [loadQueue, loadGallery]);

  const openBuild = (b) => {
    const sp = b.spec || {};
    setBuild(b); setReview(b.review || null); setActiveFile(0); setShowGallery(false); setMode(b.mode || "web");
    setModifyTarget(b);
    setTab(sp.preview_html ? "preview" : (b.mode === "brainstorm" ? "brainstorm" : (b.mode === "video" || b.mode === "cinema") ? "media" : "code"));
    setMessages([{ role: "user", text: b.prompt, mode: b.mode },
      { role: "assistant", status: "done", text: `✓ Opened “${sp.app_name || "app"}”. Send a message below to modify it.`, buildId: b.build_id }]);
  };
  const startNewApp = () => { setModifyTarget(null); setBuild(null); setMessages([]); setReview(null); };

  const updLast = (patch) => setMessages((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], ...patch }; return c; });
  const phaseLabel = (b) => {
    const map = { queued: "Queued — waiting in build queue…", starting: "Starting…", architect: "Architect — designing schema & API…",
      developing: "Developer + Designer — writing code & preview…", ideating: "Brainstorming concepts…",
      storyboard: "Storyboarding scenes…", rendering: "Rendering video…", done: "Finalizing…" };
    return map[b.phase] || "Working…";
  };

  const generate = async () => {
    const p = prompt.trim();
    if (!p) return;
    // MODIFY mode: if an editable (web/mobile) app is the current target, this message edits it.
    const target = modifyTarget && (modifyTarget.mode === "web" || modifyTarget.mode === "mobile") ? modifyTarget : null;
    const baseId = target ? target.build_id : undefined;
    const curMode = target ? target.mode : mode;
    setLoading(true);
    setBuild(baseId ? build : null);
    setPhase("queued");
    setProgress(3);
    setMessages((m) => [...m, { role: "user", text: (baseId ? "✎ " : "") + p, mode: curMode }, { role: "assistant", status: "working", text: baseId ? `Queued — modifying ${target.spec?.app_name || "your app"}…` : "Queued…", mode: curMode, progress: 3 }]);
    setPrompt("");
    try {
      const r = await api.post("/builds", { prompt: p, model, mode: curMode, attachments: attachments.map((a) => a.upload_id), base_build_id: baseId });
      const buildId = r.data.build_id;
      setAttachments([]);
      loadQueue();
      const started = Date.now();
      const seen = new Set();
      const poll = async () => {
        if (!mounted.current) return;
        try {
          const res = await api.get(`/builds/${buildId}`);
          const b = res.data;
          if (b.phase) setPhase(b.phase);
          if (typeof b.progress === "number") setProgress((prev) => Math.min(96, Math.max(b.progress, prev + 4)));
          if (b.phase && !seen.has(b.phase) && b.status !== "done" && b.status !== "error") {
            seen.add(b.phase);
            setMessages((m) => { const c = m.map((x) => x.status === "working" ? { ...x, status: "stepdone" } : x); return [...c, { role: "assistant", status: "working", text: phaseLabel(b), mode: curMode, progress: b.progress }]; });
          } else {
            updLast({ progress: b.progress });
          }
          if (b.status === "done") {
            setBuild(b); setReview(b.review || null); setActiveFile(0);
            if (b.mode === "web" || b.mode === "mobile") setModifyTarget(b);
            setTab(b?.spec?.preview_html ? "preview" : (b?.mode === "brainstorm" ? "brainstorm" : (b?.mode === "video" || b?.mode === "cinema") ? "media" : "code"));
            setLoading(false); setProgress(100);
            setMessages((m) => { const c = m.map((x) => x.status === "working" ? { ...x, status: "stepdone" } : x); return [...c, { role: "assistant", status: "done", text: `✓ ${MODES.find((mm) => mm.id === b.mode)?.label || "Build"} ${baseId ? "updated" : "ready"} — ${b?.spec?.app_name || ""}`, buildId }]; });
            loadQueue(); loadGallery();
            toast.success(`${MODES.find((mm) => mm.id === b.mode)?.label || "Build"} ${baseId ? "updated" : "ready"}`);
            return;
          }
          if (b.status === "error") {
            setLoading(false);
            updLast({ status: "error", text: b.error ? `Failed: ${b.error.slice(0, 120)}` : "Generation failed" });
            loadQueue();
            toast.error("Generation failed");
            return;
          }
          if (Date.now() - started > 600000) { setLoading(false); updLast({ status: "error", text: "Timed out" }); return; }
          setTimeout(poll, 2500);
        } catch { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 2500);
    } catch (e) {
      setLoading(false);
      updLast({ status: "error", text: e?.response?.data?.detail || "Generation failed" });
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
  };

  const [picking, setPicking] = useState(false);
  const pickConcept = async (index) => {
    if (!build) return;
    setPicking(true);
    try {
      const r = await api.post(`/builds/${build.build_id}/pick-concept`, { index });
      const buildId = r.data.build_id;
      setLoading(true); setBuild(null); setMode("web"); setPhase("queued"); setProgress(3);
      setMessages((m) => [...m, { role: "user", text: "Build this concept →", mode: "web" }, { role: "assistant", status: "working", text: "Queued…", mode: "web", progress: 3 }]);
      loadQueue();
      const poll = async () => {
        if (!mounted.current) return;
        try {
          const res = await api.get(`/builds/${buildId}`);
          const b = res.data;
          if (b.phase) setPhase(b.phase);
          if (typeof b.progress === "number") setProgress((prev) => Math.min(96, Math.max(b.progress, prev + 4)));
          updLast({ text: phaseLabel(b), progress: b.progress });
          if (b.status === "done") { setBuild(b); setModifyTarget(b); setTab(b?.spec?.preview_html ? "preview" : "code"); setLoading(false); setProgress(100); updLast({ status: "done", text: `✓ Web App ready — ${b?.spec?.app_name || ""}`, buildId }); loadQueue(); loadGallery(); toast.success("App generated from concept"); return; }
          if (b.status === "error") { setLoading(false); updLast({ status: "error", text: "Generation failed" }); return; }
          setTimeout(poll, 2500);
        } catch { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 2500);
    } catch { toast.error("Failed to build concept"); }
    setPicking(false);
  };

  const spec = build?.spec;
  const files = spec?.files || [];
  const previewHtml = spec?.preview_html || "";
  const isMedia = build && (build.mode === "video" || build.mode === "cinema");
  const isBrain = build && build.mode === "brainstorm";
  const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "");

  const openPreview = () => {
    // Safe: open via a Blob URL instead of document.write (no direct HTML injection into a live doc)
    const blob = new Blob([previewHtml || ""], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const downloadHtml = () => {
    const blob = new Blob([previewHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(spec?.app_name || "app").replace(/\s+/g, "-").toLowerCase()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [zipping, setZipping] = useState(false);
  const downloadZip = async () => {
    if (!build?.build_id) return;
    setZipping(true);
    try {
      const r = await api.get(`/builds/${build.build_id}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(spec?.app_name || "app").replace(/\s+/g, "-").toLowerCase()}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Project ZIP downloaded");
    } catch (e) { toast.error("ZIP export failed"); }
    finally { setZipping(false); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(files[activeFile]?.content || ""); } catch {}
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const onAttach = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploadingRef(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, { upload_id: r.data.upload_id, filename: r.data.filename, kind: r.data.kind }]);
      } catch { toast.error(`Failed to upload ${f.name}`); }
    }
    setUploadingRef(false);
    if (attachRef.current) attachRef.current.value = "";
  };

  const runReview = async () => {
    if (!build) return;
    setReviewing(true);
    setReview(build.review || null);
    try {
      const r = await api.post(`/builds/${build.build_id}/review`);
      setReview(r.data);
      toast.success("QA & Ultra Review complete");
    } catch (e) { toast.error(e?.response?.data?.detail || "Review failed"); }
    finally { setReviewing(false); }
  };

  return (
    <div className="flex h-screen">
      {/* Left: prompt & agents */}
      <div className="w-[360px] shrink-0 border-r border-[#2A2625]/10 bg-[#F9F6F4] flex flex-col">
        <div className="h-16 flex items-center px-6 border-b border-[#2A2625]/10">
          <Cube size={22} weight="duotone" className="text-[#F29A86]" />
          <span className="font-display font-semibold ml-2">App Builder</span>
          <button data-testid="my-apps-btn" onClick={() => { loadGallery(); setShowGallery(true); }}
            className="ml-auto flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-[#2A2625]/15 hover:bg-[#2A2625]/5 transition-colors">
            <Stack size={14} /> My Apps{gallery.length ? ` (${gallery.length})` : ""}
          </button>
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Conversation thread */}
          <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="builder-thread">
            {messages.length === 0 && (
              <div className="text-center mt-8 px-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFF4F0] flex items-center justify-center mx-auto mb-3"><Sparkle size={22} weight="duotone" className="text-[#F29A86]" /></div>
                <p className="text-sm font-medium text-[#2A2625]">Chat to build</p>
                <p className="text-xs text-[#8C8381] mt-1 mb-4">Pick a mode, describe what you want, and press send. Each request runs in the queue and appears here.</p>
                <div className="flex flex-col gap-1.5">
                  {SAMPLES.map((s) => (
                    <button key={s} onClick={() => setPrompt(s)} className="text-xs px-3 py-2 rounded-xl bg-white border border-[#2A2625]/10 text-[#594F4D] hover:bg-[#2A2625]/5 text-left transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              msg.role === "user" ? (
                <div key={i} className="flex justify-end" data-testid={`msg-user-${i}`}>
                  <div className="max-w-[85%] bg-[#FFB8A5] text-[#2A2625] rounded-2xl rounded-br-md px-3.5 py-2.5 text-sm">
                    <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{MODES.find((m) => m.id === msg.mode)?.label}</span>
                    <p className="mt-0.5 whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </div>
              ) : (
                <div key={i} className="flex justify-start" data-testid={`msg-assistant-${i}`}>
                  <div className={`max-w-[90%] rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm border ${msg.status === "error" ? "bg-[#FFF1F1] border-[#E09494]/40 text-[#9b3b3b]" : msg.status === "done" ? "bg-[#F3FBF6] border-emerald-200 text-[#2A2625]" : msg.status === "stepdone" ? "bg-[#FCFAF8] border-[#2A2625]/8 text-[#8C8381]" : "bg-white border-[#2A2625]/10 text-[#594F4D]"}`}>
                    <div className="flex items-center gap-2">
                      {msg.status === "working" && <CircleNotch size={14} className="animate-spin text-[#F29A86] shrink-0" />}
                      {msg.status === "stepdone" && <Check size={13} weight="bold" className="text-[#A3C2A4] shrink-0" />}
                      {msg.status === "done" && <Check size={14} weight="bold" className="text-emerald-600 shrink-0" />}
                      {msg.status === "error" && <Warning size={14} weight="fill" className="text-[#E09494] shrink-0" />}
                      <span>{msg.text}</span>
                    </div>
                    {msg.status === "working" && typeof msg.progress === "number" && (
                      <div className="mt-2 h-1.5 w-40 rounded-full bg-[#2A2625]/10 overflow-hidden"><div className="h-full rounded-full bg-[#F29A86] transition-all" style={{ width: `${msg.progress}%` }} /></div>
                    )}
                  </div>
                </div>
              )
            ))}
            {queue.filter((q) => q.status === "queued").map((q, i) => (
              <div key={`q${i}`} className="flex justify-start" data-testid={`queued-msg-${i}`}>
                <div className="max-w-[90%] rounded-2xl rounded-bl-md px-3.5 py-2 text-xs border border-dashed border-[#2A2625]/20 bg-[#FCFAF8] text-[#8C8381] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F4D399]" /> Queued · {MODES.find((m) => m.id === q.mode)?.label} — {q.prompt.slice(0, 40)}
                </div>
              </div>
            ))}
          </div>

          {/* Composer */}
          <div className="border-t border-[#2A2625]/10 bg-[#FCFAF8] p-3 space-y-2">
            {modifyTarget && (modifyTarget.mode === "web" || modifyTarget.mode === "mobile") ? (
              <div className="flex items-center gap-2 text-xs bg-[#FFF4F0] border border-[#F29A86]/40 rounded-lg px-3 py-2" data-testid="modify-banner">
                <PaintBrush size={14} weight="duotone" className="text-[#F29A86] shrink-0" />
                <span className="text-[#594F4D] truncate">Editing <b className="text-[#2A2625]">{modifyTarget.spec?.app_name || "app"}</b> — your next message will modify it.</span>
                <button data-testid="new-app-btn" onClick={startNewApp} className="ml-auto shrink-0 flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-[#2A2625]/15 hover:bg-[#2A2625]/5 font-medium"><Plus size={12} weight="bold" /> New app</button>
              </div>
            ) : null}
            <div className={`flex gap-1 overflow-x-auto ${modifyTarget && (modifyTarget.mode === "web" || modifyTarget.mode === "mobile") ? "opacity-40 pointer-events-none" : ""}`} data-testid="builder-modes">
              {MODES.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)} title={m.hint}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors border ${mode === m.id ? "border-[#F29A86] bg-[#FFF4F0] text-[#2A2625]" : "border-[#2A2625]/10 bg-white text-[#8C8381] hover:text-[#2A2625]"}`}>
                    <Icon size={14} weight={mode === m.id ? "duotone" : "regular"} /> {m.label}
                  </button>
                );
              })}
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {attachments.map((a, i) => (
                  <span key={a.upload_id} className="flex items-center gap-1 text-xs bg-white border border-[#2A2625]/10 rounded-full px-2.5 py-1" data-testid={`builder-attachment-${i}`}>
                    <span className="max-w-[120px] truncate">{a.filename}</span>
                    <X size={12} className="cursor-pointer hover:text-[#E09494]" onClick={() => setAttachments((l) => l.filter((_, idx) => idx !== i))} />
                  </span>
                ))}
              </div>
            )}
            <textarea data-testid="builder-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) generate(); }}
              placeholder={mode === "brainstorm" ? "Describe an idea or theme to brainstorm…" : `Describe the ${MODES.find((m) => m.id === mode)?.label.toLowerCase()} you want to build…`}
              className="w-full resize-none bg-white border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
            <div className="flex items-center gap-2">
              <select data-testid="builder-model" value={model} onChange={(e) => setModel(e.target.value)}
                className="bg-white border border-[#2A2625]/15 rounded-lg px-2 py-2 text-xs outline-none focus:ring-2 focus:ring-[#FFB8A5] max-w-[130px]">
                {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
              <input ref={attachRef} type="file" multiple className="hidden" onChange={onAttach} accept="image/*,.pdf,.txt,.csv,.md,.json,.docx" />
              <button data-testid="builder-attach-btn" onClick={() => attachRef.current?.click()} disabled={uploadingRef}
                className="p-2 rounded-lg border border-[#2A2625]/15 hover:bg-[#2A2625]/5 disabled:opacity-50" title="Attach reference files">
                {uploadingRef ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
              </button>
              <button data-testid="generate-btn" onClick={generate} disabled={loading || !prompt.trim()}
                className="ml-auto flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] disabled:opacity-40 text-[#2A2625] font-medium rounded-full px-4 py-2 text-sm transition-colors">
                {loading ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />} Send
              </button>
            </div>
            <p className="text-[10px] text-[#8C8381] text-center">Runs in a queue · ⌘/Ctrl + Enter to send</p>
          </div>
        </div>
      </div>

      {/* Right: output */}
      <div className="flex-1 min-w-0 flex flex-col relative">
        {showGallery && (
          <div className="absolute inset-0 z-30 bg-[#F9F6F4]" data-testid="apps-gallery">
            <div className="h-16 border-b border-[#2A2625]/10 bg-white flex items-center px-6 gap-3">
              <Stack size={20} weight="duotone" className="text-[#F29A86]" />
              <span className="font-display font-semibold">My Apps</span>
              <span className="text-xs text-[#8C8381]">{gallery.length} generated</span>
              <button data-testid="close-gallery-btn" onClick={() => setShowGallery(false)} className="ml-auto p-2 rounded-lg hover:bg-[#2A2625]/5"><X size={18} className="text-[#8C8381]" /></button>
            </div>
            <div className="p-6 overflow-y-auto" style={{ height: "calc(100% - 4rem)" }}>
              {gallery.length === 0 ? (
                <div className="text-center mt-16 text-sm text-[#8C8381]">No apps yet. Build one from the chat and it will appear here.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {gallery.map((b) => {
                    const M = MODES.find((m) => m.id === b.mode) || MODES[0]; const Icon = M.icon;
                    return (
                      <button key={b.build_id} data-testid={`app-card-${b.build_id}`} onClick={() => openBuild(b)}
                        className="text-left bg-white border border-[#2A2625]/10 rounded-2xl overflow-hidden hover:border-[#F29A86] hover:shadow-lg transition-all group">
                        <div className="h-28 bg-[#FCFAF8] border-b border-[#2A2625]/8 relative overflow-hidden">
                          {b.spec?.thumb_url ? (
                            <img src={BACKEND_ORIGIN + b.spec.thumb_url} alt="" className="w-full h-full object-cover" />
                          ) : b.spec?.preview_html ? (
                            <iframe title={b.build_id} srcDoc={b.spec.preview_html} sandbox="" className="w-[200%] h-[200%] origin-top-left scale-50 pointer-events-none bg-white" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Icon size={30} weight="duotone" className="text-[#F29A86]" /></div>
                          )}
                          <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-white/90 text-[#F29A86] font-medium flex items-center gap-1"><Icon size={11} /> {M.label}</span>
                        </div>
                        <div className="p-3">
                          <div className="font-medium text-sm text-[#2A2625] truncate">{b.spec?.app_name || b.prompt}</div>
                          <div className="text-xs text-[#8C8381] truncate mt-0.5">{b.prompt}</div>
                          <div className="text-[10px] text-[#8C8381] mt-2 flex items-center gap-1 group-hover:text-[#F29A86]"><ArrowRight size={11} weight="bold" /> Open & modify</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="h-16 border-b border-[#2A2625]/10 bg-white/70 backdrop-blur-xl flex items-center px-6 gap-3">
          <span className="font-display font-semibold truncate">{spec?.app_name || "Generated app"}</span>
          {build && <span className="text-xs px-2.5 py-1 rounded-full bg-[#FFF4F0] text-[#F29A86] font-medium shrink-0">{build.model}</span>}
          {build && !isMedia && !isBrain && (
            <div className="ml-auto flex items-center gap-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg p-1">
              {TABS.map((t) => (
                (t.id !== "preview" || previewHtml) && (
                  <button
                    key={t.id}
                    data-testid={`builder-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                      tab === t.id ? "bg-[#FFB8A5] text-[#2A2625]" : "text-[#594F4D] hover:text-[#2A2625]"
                    }`}
                  >
                    <t.icon size={14} weight="bold" /> {t.label}
                  </button>
                )
              ))}
            </div>
          )}
        </div>

        {!build && !loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 rounded-3xl bg-[#FFF4F0] flex items-center justify-center mx-auto mb-5">
                <Cube size={32} weight="duotone" className="text-[#F29A86]" />
              </div>
              <h2 className="font-display text-2xl font-semibold">Describe. Generate. Run.</h2>
              <p className="text-[#594F4D] mt-2">Enter an idea on the left. Agents plan the schema, design and code — then a fully working app runs live right here.</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center w-full max-w-sm" data-testid="builder-progress">
              <CircleNotch size={40} className="animate-spin text-[#F29A86] mx-auto" />
              <p className="text-[#594F4D] mt-4 font-medium">
                {phase === "architect" && "Architect agent — designing schema & API…"}
                {phase === "developing" && "Developer + Designer agents — writing full-stack code & live preview…"}
                {phase === "designing" && "Designer agent — building a live preview…"}
                {(!phase || phase === "done") && "Agents are orchestrating your app…"}
              </p>
              <div className="mt-4 h-2 w-full rounded-full bg-[#2A2625]/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#F29A86] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="builder-progress-bar" />
              </div>
              <p className="text-xs text-[#8C8381] mt-2">{progress}% · full-stack web + iOS + Android</p>
            </div>
          </div>
        )}

        {build && !loading && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {spec?.llm_unavailable && (
              <div className="mx-6 mt-4 flex items-start gap-3 rounded-xl border border-[#F4D399]/60 bg-[#FFF9EC] px-4 py-3" data-testid="scaffold-fallback-banner">
                <Warning size={18} weight="fill" className="text-[#B8860B] mt-0.5 shrink-0" />
                <div className="text-sm text-[#5c4a15]">
                  <b>AI code-gen was unavailable</b> (LLM daily budget reached), so we generated a <b>complete, runnable project scaffold</b> from your idea — backend API, frontend, Docker & run script are all in the ZIP. Top up your Universal Key (Profile → Manage plan) and hit <b>Regenerate</b> for the full AI-written code & interactive preview.
                  <button data-testid="regenerate-btn" onClick={() => generate()} className="ml-2 underline font-medium hover:text-[#B8860B]">Regenerate</button>
                </div>
              </div>
            )}
            {/* MEDIA (Long AI Video / AI Cinema) */}
            {isMedia && (
              <div className="flex-1 overflow-y-auto p-6" data-testid="media-output">
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    {build.mode === "cinema" ? <FilmSlate size={20} weight="duotone" className="text-[#F29A86]" /> : <VideoCamera size={20} weight="duotone" className="text-[#F29A86]" />}
                    <h3 className="font-display font-semibold">{spec?.app_name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFF4F0] text-[#F29A86]">{MODES.find((m) => m.id === build.mode)?.label}</span>
                  </div>
                  <video data-testid="build-video" controls className="w-full rounded-2xl border border-[#2A2625]/10 bg-black"
                    poster={spec?.thumb_url ? BACKEND_ORIGIN + spec.thumb_url : undefined}
                    src={spec?.video_url ? BACKEND_ORIGIN + spec.video_url : undefined} />
                  <p className="text-sm text-[#594F4D] mt-3">{spec?.summary}</p>
                  {spec?.video_url && (
                    <a data-testid="download-video-btn" href={BACKEND_ORIGIN + spec.video_url} download
                      className="mt-3 inline-flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 text-sm transition-colors">
                      <DownloadSimple size={16} weight="bold" /> Download MP4
                    </a>
                  )}
                </div>
              </div>
            )}
            {/* BRAINSTORM */}
            {isBrain && (
              <div className="flex-1 overflow-y-auto p-6" data-testid="brainstorm-output">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb size={20} weight="duotone" className="text-[#F29A86]" />
                  <h3 className="font-display font-semibold">{spec?.summary}</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {(spec?.concepts || []).map((c, i) => (
                    <div key={i} className="bg-white border border-[#2A2625]/10 rounded-2xl p-5 flex flex-col" data-testid={`concept-${i}`}>
                      <div className="font-display font-semibold text-[#2A2625]">{c.name}</div>
                      <div className="text-xs text-[#F29A86] font-medium mb-2">{c.tagline}</div>
                      <p className="text-sm text-[#594F4D]">{c.description}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {(c.key_features || []).map((f, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FFF4F0] text-[#594F4D]">{f}</span>)}
                      </div>
                      <div className="text-[11px] text-[#8C8381] mt-3 space-y-0.5">
                        <div><b>User:</b> {c.target_user}</div>
                        <div><b>Stack:</b> {c.suggested_stack}</div>
                        <div><b>Monetization:</b> {c.monetization}</div>
                      </div>
                      <button data-testid={`build-concept-${i}`} onClick={() => pickConcept(i)} disabled={picking}
                        className="mt-4 w-full flex items-center justify-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full py-2.5 text-sm transition-colors disabled:opacity-50">
                        <ArrowRight size={15} weight="bold" /> Build this app
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* PREVIEW */}
            {tab === "preview" && previewHtml && (
              <div className="flex-1 flex flex-col">
                <div className="flex items-center gap-2 px-4 py-2 border-b border-[#2A2625]/10 bg-[#FCFAF8]">
                  <span className="flex gap-1.5"><span className="w-3 h-3 rounded-full bg-[#E09494]" /><span className="w-3 h-3 rounded-full bg-[#F4D399]" /><span className="w-3 h-3 rounded-full bg-[#A3C2A4]" /></span>
                  <span className="text-xs text-[#8C8381] ml-2 font-mono truncate">{spec?.app_name} — live preview</span>
                  <div className="ml-auto flex gap-2">
                    <button data-testid="open-preview-btn" onClick={openPreview} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#2A2625]/15 hover:bg-[#2A2625]/5 transition-colors"><ArrowSquareOut size={14} /> Open</button>
                    <button data-testid="download-html-btn" onClick={downloadHtml} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FFB8A5] hover:bg-[#FFCBBF] transition-colors"><DownloadSimple size={14} weight="bold" /> Download</button>
                  </div>
                </div>
                <iframe
                  data-testid="preview-iframe"
                  title="app-preview"
                  srcDoc={previewHtml}
                  sandbox="allow-scripts allow-forms allow-modals allow-popups"
                  className="flex-1 w-full bg-white"
                />
              </div>
            )}

            {/* CODE */}
            {tab === "code" && (
              <div className="flex-1 overflow-y-auto p-6">
                <div className="bg-white border border-[#2A2625]/10 rounded-2xl overflow-hidden">
                  <div className="flex items-center gap-1 border-b border-[#2A2625]/10 px-3 py-2 overflow-x-auto z-scroll-hide">
                    {files.map((f, i) => (
                      <button
                        key={i}
                        data-testid={`file-tab-${i}`}
                        onClick={() => setActiveFile(i)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono whitespace-nowrap transition-colors ${
                          activeFile === i ? "bg-[#FFB8A5]/30 text-[#2A2625]" : "text-[#594F4D] hover:bg-[#2A2625]/5"
                        }`}
                      >
                        <File size={13} /> {f.path?.split("/").pop()}
                      </button>
                    ))}
                    <button onClick={copyCode} className="ml-auto text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#2A2625]/15 hover:bg-[#2A2625]/5 transition-colors shrink-0">
                      {copied ? <Check size={13} weight="bold" /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                    </button>
                    <button data-testid="download-zip-btn" onClick={downloadZip} disabled={zipping} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2A2625] text-white hover:bg-[#2A2625]/85 transition-colors shrink-0">
                      {zipping ? <CircleNotch size={13} className="animate-spin" /> : <DownloadSimple size={13} weight="bold" />} Download ZIP
                    </button>
                  </div>
                  <p className="px-4 py-1.5 text-[11px] font-mono text-[#8C8381] border-b border-[#2A2625]/8">{files[activeFile]?.path}</p>
                  <pre className="text-[13px] leading-relaxed p-5 overflow-x-auto bg-[#2A2625] text-[#FCE9E2] font-mono max-h-[calc(100vh-220px)]" data-testid="code-viewer">
                    <code>{files[activeFile]?.content}</code>
                  </pre>
                </div>
              </div>
            )}

            {/* BLUEPRINT */}
            {tab === "blueprint" && (
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <p className="text-[#594F4D]">{spec?.summary}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><Database size={18} weight="duotone" className="text-[#F29A86]" /><h3 className="font-display font-semibold">Schema & APIs</h3></div>
                    <div className="space-y-2">
                      {(spec?.architect?.schema || []).map((c, i) => (
                        <div key={i} className="text-sm">
                          <span className="font-mono font-medium">{c.collection}</span>
                          <span className="text-[#8C8381] font-mono text-xs"> — {(c.fields || []).join(", ")}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 space-y-1">
                      {(spec?.architect?.api || []).map((a, i) => (
                        <div key={i} className="text-xs font-mono flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-[#FFF4F0] text-[#F29A86] font-semibold">{a.method}</span>
                          <span className="text-[#594F4D]">{a.path}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><PaintBrush size={18} weight="duotone" className="text-[#A3C2A4]" /><h3 className="font-display font-semibold">Design system</h3></div>
                    <p className="text-sm text-[#594F4D]">{spec?.designer?.theme}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="w-5 h-5 rounded-md border border-[#2A2625]/10" style={{ background: spec?.designer?.primary_color }} />
                      <span className="text-xs font-mono">{spec?.designer?.primary_color}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(spec?.designer?.components || []).map((c, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#FCFAF8] border border-[#2A2625]/10">{c}</span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(spec?.architect?.screens || []).map((s, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#FFF4F0] text-[#F29A86] flex items-center gap-1"><ArrowRight size={10} />{s}</span>
                      ))}
                    </div>
                    {spec?.platform && <p className="text-xs text-[#8C8381] mt-3 font-mono">{spec.platform}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* QA REVIEW */}
            {tab === "review" && (
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#FFF4F0] flex items-center justify-center"><ShieldCheck size={26} weight="duotone" className="text-[#F29A86]" /></div>
                  <div className="flex-1">
                    <h3 className="font-display font-semibold">Multi-Agent QA & Ultra Reviewer</h3>
                    <p className="text-sm text-[#594F4D]">QA · Security · UX · adversarial Ultra pass with extended reasoning</p>
                  </div>
                  <button data-testid="run-review-btn" onClick={runReview} disabled={reviewing} className="flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] disabled:opacity-40 text-[#2A2625] font-medium rounded-full px-5 py-2.5 transition-colors">
                    {reviewing ? <><CircleNotch size={18} className="animate-spin" /> Reviewing…</> : <><Bug size={18} weight="fill" /> Run review</>}
                  </button>
                </div>
                {review && (
                  <>
                    <div className="bg-[#2A2625] text-white rounded-2xl p-6 flex items-center gap-5">
                      <div className="text-center"><p className="text-4xl font-display font-semibold text-[#FFB8A5]" data-testid="review-score">{review.score}</p><p className="text-xs text-white/60">Quality score</p></div>
                      <p className="text-sm text-white/80 flex-1">{review.verdict}</p>
                    </div>
                    <div className="space-y-2" data-testid="review-findings">
                      {(review.findings || []).map((f, i) => (
                        <div key={i} className="bg-white border border-[#2A2625]/10 rounded-xl p-4">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${f.severity === "high" ? "bg-[#E09494]/25 text-[#b5573f]" : f.severity === "medium" ? "bg-[#F4D399]/30 text-[#8a6d1f]" : "bg-[#A3C2A4]/25 text-[#4b7a4d]"}`}>{f.severity}</span>
                            <span className="text-xs font-medium text-[#8C8381]">{f.agent}</span>
                          </div>
                          <p className="text-sm font-medium mt-2">{f.issue}</p>
                          <p className="text-sm text-[#594F4D] mt-1"><span className="text-[#F29A86] font-medium">Fix:</span> {f.fix}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {!review && !reviewing && <p className="text-sm text-[#8C8381] text-center py-8">Run the reviewer to get a quality score and prioritized findings.</p>}
              </div>
            )}

            {/* PUBLISH */}
            {tab === "publish" && (
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-6">
                  <div className="flex items-center gap-2"><RocketLaunch size={20} weight="duotone" className="text-[#F29A86]" /><h3 className="font-display font-semibold">Ship to the App Store & Play Store (EAS)</h3></div>
                  <p className="text-sm text-[#594F4D] mt-2">Zeyrodha generates the Expo config below. Run these commands with the Expo CLI to build and submit your app. Web is already live in the preview.</p>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button data-testid="open-preview-publish" onClick={openPreview} className="flex items-center gap-2 text-sm bg-[#2A2625] text-white rounded-full px-4 py-2.5 hover:bg-[#2A2625]/85 transition-colors"><Globe size={16} /> Open live web preview</button>
                    <a href="https://appstoreconnect.apple.com" target="_blank" rel="noreferrer" data-testid="link-appstore" className="flex items-center gap-2 text-sm border border-[#2A2625]/15 rounded-full px-4 py-2.5 hover:bg-[#2A2625]/5 transition-colors"><AppStoreLogo size={18} weight="fill" /> App Store Connect <ArrowSquareOut size={13} /></a>
                    <a href="https://play.google.com/console" target="_blank" rel="noreferrer" data-testid="link-playstore" className="flex items-center gap-2 text-sm border border-[#2A2625]/15 rounded-full px-4 py-2.5 hover:bg-[#2A2625]/5 transition-colors"><GooglePlayLogo size={18} weight="fill" /> Google Play Console <ArrowSquareOut size={13} /></a>
                    <a href="https://expo.dev" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm border border-[#2A2625]/15 rounded-full px-4 py-2.5 hover:bg-[#2A2625]/5 transition-colors"><RocketLaunch size={16} /> Expo Dashboard <ArrowSquareOut size={13} /></a>
                  </div>
                </div>

                {[
                  { name: "app.json", content: JSON.stringify({ expo: { name: spec?.app_name || "MyApp", slug: (spec?.app_name || "myapp").replace(/\s+/g, "-").toLowerCase(), version: "1.0.0", orientation: "portrait", icon: "./assets/icon.png", splash: { image: "./assets/splash.png", resizeMode: "contain", backgroundColor: spec?.designer?.primary_color || "#FFB8A5" }, ios: { supportsTablet: true, bundleIdentifier: `ai.zeyrodha.${(spec?.app_name || "app").replace(/\s+/g, "").toLowerCase()}` }, android: { package: `ai.zeyrodha.${(spec?.app_name || "app").replace(/\s+/g, "").toLowerCase()}` }, extra: { eas: { projectId: "your-eas-project-id" } } } }, null, 2) },
                  { name: "eas.json", content: JSON.stringify({ cli: { version: ">= 5.0.0" }, build: { development: { developmentClient: true, distribution: "internal" }, preview: { distribution: "internal" }, production: {} }, submit: { production: {} } }, null, 2) },
                ].map((f) => (
                  <div key={f.name} className="bg-white border border-[#2A2625]/10 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-[#2A2625]/10">
                      <span className="text-xs font-mono">{f.name}</span>
                      <button onClick={() => { navigator.clipboard.writeText(f.content); toast.success(`${f.name} copied`); }} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#2A2625]/15 hover:bg-[#2A2625]/5 transition-colors"><Copy size={13} /> Copy</button>
                    </div>
                    <pre className="text-[12px] p-4 overflow-x-auto bg-[#2A2625] text-[#FCE9E2] font-mono max-h-64"><code>{f.content}</code></pre>
                  </div>
                ))}

                <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-3"><Terminal size={18} weight="duotone" className="text-[#F29A86]" /><h3 className="font-display font-semibold">Build & Submit steps</h3></div>
                  <ol className="space-y-2 text-sm">
                    {[
                      "npm install -g eas-cli && eas login",
                      "eas build:configure",
                      "eas build --platform ios --profile production",
                      "eas build --platform android --profile production",
                      "eas submit --platform ios --latest",
                      "eas submit --platform android --latest",
                    ].map((c, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="w-6 h-6 shrink-0 rounded-full bg-[#FFF4F0] text-[#F29A86] text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                        <code className="flex-1 font-mono text-xs bg-[#FCFAF8] border border-[#2A2625]/10 rounded-lg px-3 py-2">{c}</code>
                        <button onClick={() => { navigator.clipboard.writeText(c); toast.success("Copied"); }} className="text-[#8C8381] hover:text-[#2A2625]"><Copy size={14} /></button>
                      </li>
                    ))}
                  </ol>
                  <p className="text-xs text-[#8C8381] mt-4">Note: store submission runs from your machine with your Apple / Google developer accounts — Zeyrodha prepares the code and config; the final publish is your one-tap step.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
